<template>
  <v-container class="py-1 bg-grey-lighten-4" fluid>
    <v-text-field
      v-model="sWord"
      :loading="loading"
      label="Suchen"
      variant="solo"
      @keypress.enter="loadArticles"
      hide-details
    >
    </v-text-field>
  </v-container>
  <template v-if="!loading && !error">
    <v-list lines="two" class="pa-0 border-t">
      <template v-for="article in articles" :key="article.ArtikelNr">
        <article-list-item :article="article"/>
        <hr
          role="separator"
          aria-orientation="horizontal"
          class="v-divider"
          theme="light"
        />
      </template>
    </v-list>
  </template>
  <template v-else-if="error">
    <v-container class="py-1" fluid>
      <error :module="error" />
    </v-container>
  </template>
  <template v-else>
    <loading/>
  </template>
</template>

<script lang="ts" setup>
import {findArticles} from '@/api/module/article';
import {useScanner} from '@/store/scanner';
import {ref, watch} from 'vue';
import Article from '@/model/Article';
import {useRoute} from 'vue-router';
import ArticleListItem from '@/components/Article/ArticleListItem.vue';
import Loading from '@/components/Loading.vue';

const scanner = useScanner();
const sWord = ref('');
const articles = ref<Article[]>([]);
const error = ref<Error | null>(null);
const loading = ref(false);
const scanning = ref(false);
const route = useRoute();

let currentSWord: string | null = null;

let debounceTimeout: number | null = null;

async function loadArticles() {
  if (debounceTimeout) clearTimeout(debounceTimeout);
  if (!sWord.value) return;
  if (currentSWord === sWord.value) return;
  currentSWord = sWord.value;

  loading.value = true;
  try {
    const [foundArticles] = await findArticles(sWord.value);

    articles.value = foundArticles.map((a) => Article.fromApi(a));

    if (articles.value.length == 1) {
      detail(articles.value[0]);
    }
  } catch (err: any) {
    error.value = err as Error;
  } finally {
    loading.value = false;
  }
}

function detail(item: Article) {
  router.push({name: 'article_info_detail', params: {id: `${item.ArtikelNr}`}}).catch(console.error);
}

scanner.addScanListener(async (event) => {
  if (scanning.value) return;
  scanning.value = true;

  sWord.value = event.data;

  scanning.value = false;
});

watch(sWord, () => {
  if (debounceTimeout) clearTimeout(debounceTimeout);
  debounceTimeout = setTimeout(() => loadArticles(), 500);
});

sWord.value = route.query.search as string ?? '';

const opened = ref<string | null>(null);

defineExpose({
  ArticleListItem,
  sWord,
  articles,
  error,
  loading,
  loadArticles,
  opened,
  toggleOpened: (id: string, val: boolean) => {
    if (id === opened.value && !val) return opened.value = null;
    if (val) opened.value = id;
  },
});
</script>
